import type { Sort } from '@/stores/sorting';
import type {
    TimeEntryBulkUpsertRequest,
    TimeEntryCollectionResponse,
    TimeEntryReportParams,
    TimeEntryReportSummaryResponse,
    TimeEntryRequest,
    TimeEntryResponse,
} from '@/types';
import { sortToQuery } from '@/utils/misc.ts';
import type { AxiosInstance, AxiosResponse } from 'axios';

class TimeEntryService {
    private readonly apiService;

    constructor(apiService: AxiosInstance) {
        this.apiService = apiService;
    }

    getPath(id?: string) {
        return id ? `/api/time-entries/${id}` : '/api/time-entries';
    }

    index(page: number, filter?: object, sort: null | Sort = null, perPage?: number) {
        return this.apiService.get(this.getPath(), {
            params: { page, per_page: perPage, ...filter, ...sortToQuery(sort) },
        });
    }

    week(params: {
        company_id: string;
        date_from: string;
        date_to: string;
    }): Promise<AxiosResponse<TimeEntryCollectionResponse>> {
        return this.apiService.get('/api/time-entries/week', { params });
    }

    show(id: string): Promise<AxiosResponse<TimeEntryResponse>> {
        return this.apiService.get(this.getPath(id));
    }

    store(request: TimeEntryRequest): Promise<AxiosResponse<TimeEntryResponse>> {
        return this.apiService.post(this.getPath(), request);
    }

    update(id: string, request: Partial<TimeEntryRequest>): Promise<AxiosResponse<TimeEntryResponse>> {
        return this.apiService.put(this.getPath(id), request);
    }

    delete(id: string): Promise<AxiosResponse<void>> {
        return this.apiService.delete(this.getPath(id));
    }

    bulkUpsert(request: TimeEntryBulkUpsertRequest): Promise<AxiosResponse<TimeEntryCollectionResponse>> {
        return this.apiService.post('/api/time-entries/bulk-upsert', request);
    }

    toInvoice(payload: {
        company_id: string;
        customer_id?: string | null;
        date_created?: string | null;
        entry_ids: string[];
    }): Promise<AxiosResponse> {
        return this.apiService.post('/api/time-entries/to-invoice', payload);
    }

    reportSummary(params: TimeEntryReportParams): Promise<AxiosResponse<TimeEntryReportSummaryResponse>> {
        return this.apiService.get('/api/time-entry-reports/summary', { params });
    }
}

export default TimeEntryService;
